import { useState } from 'react';
import { Tabs } from '@zoopla-eng/zds-components/tabs';
import { SearchMedium } from '@zoopla-eng/zds-components/icons';
import {
    LocationSearchInput,
    QueryType
} from '@zoopla/listing-search-location-search-input';
import { Box } from '@zoopla-eng/zds-components/box';
import { ContentfulImageWrapper } from '../../queries/heroContent.types';
import { content } from './constants';
import BackgroundImage from './HomepageHero.png';
import { useRouter } from 'next/router';
import { triggerTracking } from '@zoopla/tracking';
import { ButtonLink, ButtonLeft } from '@zoopla-eng/zds-components/button';
import { useOptimizelyFeatureFlag } from '@zoopla/optimizely';

import {
    locationSearchInputWrapperWrapperStyle,
    ctaContainerStyle,
    heroContainerStyle,
    heroImageStyle,
    heroSpacerStyle,
    heroWrapperStyle,
    subHeaderStyle,
    tabWrapperStyle,
    headlineStyle,
    buttonTextStyle,
    formContentStyle,
    buttonContainerStyle,
    locationSearchInputWrapperStyle,
    backgroundGradient,
    heroOffset,
    heroImageElement
} from './TabbedSearch.styles.css';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import {
    getResultsPageUrl,
    navigateToResultsPage
} from '@zoopla/listing-search-utils';
import Head from 'next/head';
import { assignInlineVars } from '@vanilla-extract/dynamic';

const {
    missingSearchAreaError,
    searchAreaPlaceholder,
    saleQueryParams,
    rentQueryParams,
    housePricesQueryParams
} = content;

type TabContentProps = {
    searchForm: string;
};

const buildSearchParams = (
    searchAreaInputValue: string | undefined,
    searchForm: string,
    selectedSearchArea: { id: string; text: string } | null
) => {
    let queryParams;

    if (searchForm === 'for-sale') {
        queryParams = saleQueryParams;
    } else if (searchForm === 'to-rent') {
        queryParams = rentQueryParams;
    } else {
        queryParams = housePricesQueryParams;
    }

    // the geo identifier from the URL path should be passed as identifier, the location search field as q, the original value of that (i.e. what was there when the page loaded) as orig_q, and whatever the user selected from the autocomplete (if they did) as geo_autocomplete_identifier
    return {
        ...queryParams,
        ...(!selectedSearchArea?.id
            ? {
                  q: searchAreaInputValue ?? ''
              }
            : {
                  q: selectedSearchArea.text,
                  geo_autocomplete_identifier: selectedSearchArea?.id ?? ''
              }),
        search_source: 'home'
    };
};

const TabContent = ({ searchForm }: TabContentProps) => {
    const router = useRouter();
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedSearchArea, setSelectedSearchArea] = useState<{
        id: string;
        text: string;
    } | null>(null);
    const [searchAreaInputValue, setSearchAreaInputValue] = useState<
        string | undefined
    >();

    const searchValue = selectedSearchArea?.text || searchAreaInputValue || '';

    // upon selecting a geolocation pre-fetch the LSRP assuming the user is likely
    // to submit the query thus improving perceived speed
    const handleSelectionChange = async (
        val: { id: string; text: string } | null
    ) => {
        setSelectedSearchArea(val);

        const searchValue = val?.id || searchAreaInputValue || '';

        if (!searchValue) {
            return;
        }

        const params = buildSearchParams(searchValue, searchForm, val);

        const lsrpUrl = await getResultsPageUrl(params);
        if (!lsrpUrl) return;
        router.prefetch(lsrpUrl);
    };

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        setSubmitted(true);

        const searchValue =
            selectedSearchArea?.id || searchAreaInputValue || '';

        if (!searchValue) {
            setLoading(false);
            return;
        }

        setLoading(true);
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'homepage',
            eventAction: 'search',
            eventLabel: `${searchForm}-success`
        });

        const params = buildSearchParams(
            searchAreaInputValue,
            searchForm,
            selectedSearchArea
        );

        navigateToResultsPage(params, router.push).then(() =>
            setLoading(false)
        );
    };

    return (
        <form onSubmit={handleSubmit} role="search">
            <div className={formContentStyle}>
                <div
                    className={locationSearchInputWrapperWrapperStyle({
                        type: 'notHalf'
                    })}
                >
                    <div
                        className={locationSearchInputWrapperStyle}
                        data-testid="autosuggest-search-input"
                    >
                        <LocationSearchInput
                            onSelectedItemChange={handleSelectionChange}
                            onInputValueChange={setSearchAreaInputValue}
                            label={'Enter a city, town or postcode'}
                            placeholder={searchAreaPlaceholder}
                            errorText={
                                submitted && !searchValue
                                    ? missingSearchAreaError
                                    : undefined
                            }
                            queryType={
                                searchForm === 'house-prices'
                                    ? QueryType.AddressPostcodes
                                    : QueryType.GeoLocations
                            }
                        />
                    </div>
                </div>
                <div
                    className={ctaContainerStyle({
                        type: 'notHalf'
                    })}
                >
                    <ButtonLeft
                        category="primary"
                        Icon={SearchMedium}
                        data-testid="search-btn"
                        loading={loading}
                        loadingText={'Searching'}
                    >
                        Search
                    </ButtonLeft>
                </div>
            </div>
        </form>
    );
};

const HomeValueTabContent = () => {
    return (
        <Box
            fontGroup="ladle"
            px="S400"
            pt="S200"
            pb="S100"
            bpM100={{
                py: 'S100'
            }}
        >
            <div className={buttonContainerStyle}>
                <strong className={buttonTextStyle}>
                    First, let’s find your home to see how much it’s worth
                </strong>
                <ButtonLink
                    fw
                    category="primary"
                    data-testid="my-home-cta"
                    href="/my-home/?search_source=home"
                >
                    Find my home
                </ButtonLink>
            </div>
        </Box>
    );
};

type TabbedSearchProps = {
    image: ContentfulImageWrapper;
    brandHeadline: string;
    h1TitleTag: string;
    isLoggedIn?: boolean;
};

export const TabbedSearch = ({
    brandHeadline,
    image,
    h1TitleTag,
    isLoggedIn
}: TabbedSearchProps) => {
    const [value, updateValue] = useState('for-sale');
    const [homeValueFeature] = useOptimizelyFeatureFlag('_home-value-search');

    const homeValueTabIsEnabled = homeValueFeature === true;

    const handleTabSelect = (selectedTab: string) => {
        updateValue(selectedTab);
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'homepage',
            eventAction: 'channel_tab',
            eventLabel: `${selectedTab}`
        });
    };

    const imageQuality = 65;
    const imageWidths = [320, 550, 768, 1024, 1272, 1440, 1900];
    const heroImage = image.url ?? BackgroundImage;

    const avifSrcSet = imageWidths.map(
        (width) => `${heroImage}?w=${width}&fm=avif&q=${imageQuality} ${width}w`
    );
    const webpSrcSet = imageWidths.map(
        (width) => `${heroImage}?w=${width}&fm=webp&q=${imageQuality} ${width}w`
    );

    const focalPoint_y = (image.focalPoint.y / image.height) * 100;

    return (
        <>
            <Head>
                <link
                    rel="preload"
                    as="image"
                    href=""
                    imageSrcSet={avifSrcSet.join(', ')}
                    imageSizes="100vw"
                />
                <link
                    rel="preload"
                    as="font"
                    href={`${
                        process.env.NODE_ENV === 'production'
                            ? 'https://www.zoopla.co.uk'
                            : ''
                    }/fonts/ZooplaDisplay-Bold.woff2`}
                    type="font/woff2"
                    crossOrigin="anonymous"
                />
            </Head>
            <Box fw>
                <div className={heroWrapperStyle({ isLoggedIn })}>
                    {!isLoggedIn && (
                        <>
                            <div className={backgroundGradient}></div>
                            <picture className={heroImageStyle}>
                                <source
                                    type="image/avif"
                                    sizes="100vw"
                                    srcSet={avifSrcSet.join(', ')}
                                />
                                <source
                                    type="image/webp"
                                    sizes="100vw"
                                    srcSet={webpSrcSet.join(', ')}
                                />
                                <img
                                    src={`${heroImage}?w=1024&fm=jpg&q=75`}
                                    alt=""
                                    className={heroImageElement}
                                    style={assignInlineVars({
                                        [heroOffset]: `${focalPoint_y}%`
                                    })}
                                />
                            </picture>
                        </>
                    )}
                    <div className={heroContainerStyle({ isLoggedIn })}>
                        {!isLoggedIn && (
                            <p
                                className={`${headlineStyle} ${fontGroups.teapot}`}
                                data-testid="homepage-headline"
                            >
                                {brandHeadline
                                    ? brandHeadline
                                    : 'We know what a home is really worth'}
                            </p>
                        )}

                        <h1
                            className={`${subHeaderStyle({ isLoggedIn })} ${
                                isLoggedIn ? fontGroups.mug : fontGroups.whisk
                            }`}
                            data-testid="homepage-subheader"
                        >
                            {h1TitleTag
                                ? h1TitleTag
                                : 'Find homes to buy or rent and check house prices'}
                        </h1>
                        <div className={tabWrapperStyle({ isLoggedIn })}>
                            <Tabs
                                label="Houses"
                                value={value}
                                alignment={{ default: 'centered' }}
                                tabs={[
                                    {
                                        value: 'for-sale',
                                        label: 'For sale',
                                        component: (
                                            <TabContent searchForm={value} />
                                        )
                                    },
                                    {
                                        value: 'to-rent',
                                        label: 'To rent',
                                        component: (
                                            <TabContent searchForm={value} />
                                        )
                                    },
                                    homeValueTabIsEnabled
                                        ? {
                                              value: 'home-value',
                                              label: 'Home value',
                                              component: <HomeValueTabContent />
                                          }
                                        : {
                                              value: 'house-prices',
                                              label: 'House prices',
                                              component: (
                                                  <TabContent
                                                      searchForm={value}
                                                  />
                                              )
                                          }
                                ]}
                                onValueChange={handleTabSelect}
                            />
                        </div>
                    </div>
                </div>
                <div
                    className={heroSpacerStyle({
                        isLoggedIn
                    })}
                />
            </Box>
        </>
    );
};
